// Frontend mirror of the contract Reports wire shapes defined in
// backend/src/models/report.ts.
//
// TYPES ONLY — deliberately no derivation, no formatting, no defaults. Every
// report model is composed server-side and rendered verbatim, so there is
// nothing here to keep in parity with the backend beyond the shapes
// themselves and no ADR-015 fixture to maintain. If a number needs to change,
// it changes in the composer.
//
// Where a report field reuses a domain type the frontend already mirrors
// (grade, finance rollups, contract status, tracking enums, milestone
// lifecycle), this module imports that type rather than redeclaring it — a
// second declaration would drift silently.
//
// Two conventions carried over from the backend model:
//   - Every summary section carries a `status`. An `unavailable` section
//     carries NOTHING else, so a consumer can never read a half-populated
//     section as real data — and a missing number can never render as zero.
//   - Every currency value is ex-GST dollars.
//
// Status-like fields the backend types as bare `string` (purchase-order,
// variation and SOW statuses) stay `string` here too. They are echoed from
// stored records that predate the current enums, so a consumer must treat an
// unrecognised value as displayable text rather than assuming a badge exists
// for it.

import type { GradeResult } from "../lib/contract-overview/contractGrade";
import type { ContractRollup, PoPosition } from "./finance";
import type { ManagedContractStatus, ExtensionOptions } from "./managedContract";
import type {
  InsuranceCategory,
  InsuranceType,
  IssueSeverity,
  IssueStatus,
  VariationTarget,
} from "./tracking";
import type { MilestoneDisplayStatusV2 } from "./tracking";

// ─── Shared primitives ────────────────────────────────────────────────────

export const REPORT_RANGES = ["1m", "3m", "6m", "12m", "all"] as const;
export type ReportRange = (typeof REPORT_RANGES)[number];

/** A calendar month key, `YYYY-MM` — the canonical axis shared by delivery
 *  buckets, the KPI heatmap, financial months and service credits. */
export type ReportMonth = string;

export type ReportRating = "green" | "amber" | "red";

// ─── Commentary ───────────────────────────────────────────────────────────

export type ManagerRag = ReportRating | null;

export interface ReportCommentary {
  overallSummary: string;
  keyRisks: string;
  nextFocus: string;
  managerRag: ManagerRag;
  /** 0 when no record exists yet. A PUT carrying 0 creates the first record;
   *  any other value must match the stored version or the write is a 409. */
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
}

/** The PUT body. The server ignores anything else on the wire. */
export interface ReportCommentaryPutInput {
  overallSummary: string;
  keyRisks: string;
  nextFocus: string;
  managerRag: ManagerRag;
  version: number;
}

/** Maximum accepted length per commentary field, mirroring the server's Zod
 *  cap so the editor can stop the user before a 400 does. */
export const COMMENTARY_FIELD_MAX = 4000;

// ─── Service credits ──────────────────────────────────────────────────────

export interface ServiceCredit {
  month: ReportMonth;
  /** Ex-GST dollars, non-negative. Zero with a note records an explicitly
   *  waived credit, which is different from no record at all. */
  amount: number;
  note: string;
  version: number;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
}

/** The PUT body. `version: 0` creates the month's first record; any other
 *  value must match the stored version or the write is a 409. */
export interface ServiceCreditPutInput {
  amount: number;
  note: string;
  version: number;
}

/** The DELETE body. A credit is a monetary decision, so removing one takes
 *  both the version it was read at and a stated reason — the reason is what
 *  the audit entry carries. */
export interface ServiceCreditDeleteInput {
  version: number;
  reason: string;
}

/** Maximum accepted length for a credit note and a deletion reason,
 *  mirroring the server's Zod caps so the form can stop the user before a
 *  400 does. */
export const SERVICE_CREDIT_NOTE_MAX = 500;

// ─── Delivery performance ─────────────────────────────────────────────────

/** The record-type filter, as a value list because the filter chips and the
 *  URL guard both need it at runtime. */
export const DELIVERY_RECORD_TYPES = [
  "all",
  "milestone",
  "deliverable",
] as const;
export type DeliveryRecordType = (typeof DELIVERY_RECORD_TYPES)[number];

export interface DeliveryBucket {
  month: ReportMonth;
  due: number;
  onTime: number;
  late: number;
  stillOverdue: number;
  /** The month's on-time percentage over its delivered items only. Null below
   *  the server's minimum sample, and for a month with nothing resolved —
   *  render it as a dash rather than computing a rate here. */
  onTimeRate: number | null;
}

export interface DeliveryLateItem {
  id: string;
  name: string;
  recordType: "milestone" | "deliverable";
  dueDate: string;
  deliveredDate: string | null;
  slipDays: number;
  outcome: "late" | "stillOverdue";
}

export interface DeliveryMetrics {
  /** null below the server's minimum sample — a rate over one or two items
   *  reads as precision the data does not carry. */
  onTimeRate: number | null;
  onTimeCount: number;
  totalResolved: number;
  lateCount: number;
  medianSlipDays: number | null;
  stillOverdueCount: number;
  oldestOverdueDays: number | null;
  prevRangeOnTimeRate: number | null;
}

export interface DeliveryReportModel {
  range: ReportRange;
  recordType: DeliveryRecordType;
  generatedAt: string;
  metrics: DeliveryMetrics;
  buckets: DeliveryBucket[];
  lateItems: DeliveryLateItem[];
}

// ─── KPI compliance ───────────────────────────────────────────────────────

export type KpiCellState =
  | "met"
  | "atRisk"
  | "breached"
  | "notDue"
  | "notClosed";

export interface KpiCellResult {
  period: string;
  rating: ReportRating;
  value: string;
}

export interface KpiHeatmapCell {
  month: ReportMonth;
  state: KpiCellState;
  results: KpiCellResult[];
}

export interface KpiHeatmapRow {
  kpiId: string;
  kpiName: string;
  target: string;
  cells: KpiHeatmapCell[];
}

export interface KpiDetailRow {
  kpiId: string;
  kpiName: string;
  target: string;
  metCount: number;
  measuredCount: number;
  streakMonths: number;
}

export interface KpiComplianceValue {
  month: ReportMonth;
  weightedPct: number | null;
}

export interface KpiReportModel {
  range: ReportRange;
  generatedAt: string;
  metrics: {
    lastClosedMonth: ReportMonth | null;
    lastMonthMet: number;
    lastMonthMeasured: number;
    lastMonthWeightedPct: number | null;
    rangeWeightedPct: number | null;
    breachCount: number;
    atRiskCount: number;
    creditsTotal: number;
    breachedMonths: number;
    costedBreachedMonths: number;
  };
  complianceSeries: KpiComplianceValue[];
  heatmap: KpiHeatmapRow[];
  kpiDetail: KpiDetailRow[];
  credits: ServiceCredit[];
}

// ─── Financial performance ────────────────────────────────────────────────

export interface FinancialMonthRow {
  month: ReportMonth;
  invoiceCount: number;
  invoicedTotal: number;
  /** The approved portion OF INVOICES DATED THIS MONTH — not of approvals
   *  that happened this month. */
  approvedTotal: number;
  avgApprovalDays: number | null;
}

/**
 * The contract's consumed-versus-remaining position on an INVOICE basis.
 * `remaining` is derived from invoices, never from purchase-order commitment
 * — the two answer different questions and mixing them reads a fully
 * committed PO as a fully consumed contract.
 *
 * Invariant held by the server:
 *   approved + invoicedNotApproved + remaining − overrun === contractValue
 */
export interface FinancialCumulative {
  approved: number;
  invoicedNotApproved: number;
  remaining: number;
  overrun: number;
  contractValue: number;
  termElapsedPct: number | null;
  valueConsumedPct: number | null;
}

export interface FinancialMetrics {
  invoicedToDate: number;
  approvedToDate: number;
  /** Invoice date to approval, whole days, over invoices approved in range. */
  avgApprovalDays: number | null;
  inReviewValue: number;
  inReviewCount: number;
  oldestInReviewDays: number | null;
}

export interface FinancialReportModel {
  range: ReportRange;
  generatedAt: string;
  metrics: FinancialMetrics;
  monthly: FinancialMonthRow[];
  cumulative: FinancialCumulative;
  /** Carried on the wire rather than assumed by the client, so every currency
   *  figure on the view states the basis it was derived on. */
  gstBasis: "ex-GST";
}

// ─── Summary view ─────────────────────────────────────────────────────────

/** Marks a section whose underlying read failed. Carries no data at all. */
export interface SectionUnavailable {
  status: "unavailable";
}

export interface SummaryBandSection {
  status: "ok";
  grade: GradeResult;
  managerRag: ManagerRag;
  managerRagSetBy: string | null;
  managerRagSetAt: string | null;
  keyNumbers: {
    contractValue: number;
    termElapsedPct: number | null;
    overdueItems: number;
    openIssues: number;
    /** null when the finance read failed — one honest gap rather than a
     *  whole band withheld for a single number. Never render it as zero. */
    invoicesInReview: number | null;
  };
}

/** Contract value deliberately absent: it appears exactly twice on the
 *  summary — abbreviated in the band, at full precision in the financial
 *  section. */
export interface SummaryEssentialsSection {
  status: "ok";
  supplierName: string;
  contractNumber: string;
  title: string;
  contractTypeLabel: string;
  contractStatus: ManagedContractStatus;
  businessUnitLabel: string | null;
  procurementMethodLabel: string | null;
  managerName: string | null;
  ownerName: string | null;
  startDate: string;
  endDate: string;
  termElapsedPct: number | null;
  extensionOptions: ExtensionOptions | null;
}

export interface SummaryPurchaseOrderRow {
  id: string;
  poNumber: string;
  description: string | null;
  status: string;
  totalValue: number;
  position: PoPosition;
}

export interface SummaryFinancialSection {
  status: "ok";
  contractValue: number;
  valueBreakdown: { head: number; sows: number; variations: number };
  invoicedToDate: number;
  approvedToDate: number;
  inReviewValue: number;
  inReviewCount: number;
  cumulative: FinancialCumulative;
  /** Purchase-order commitment against contract value — a different basis
   *  from `cumulative` and labelled as such wherever it renders. */
  contractRollup: ContractRollup;
  purchaseOrders: SummaryPurchaseOrderRow[];
  gstBasis: "ex-GST";
}

/**
 * A milestone needing attention: overdue, or falling due inside the upcoming
 * horizon.
 *
 * The row carries the full DISPLAY status — lifecycle plus risk overlay — not
 * the bare lifecycle. The lifecycle alone reads 'upcoming' for a milestone
 * that is weeks overdue, which on a management report states the opposite of
 * the truth.
 *
 * There is deliberately no delivered-late slip field: this row set is the
 * overdue and not-yet-due milestones, and a milestone delivered late is
 * neither, so such a row cannot occur here.
 */
export interface SummaryMilestoneRow {
  id: string;
  name: string;
  milestoneNumber: number | null;
  milestoneSuffix: string | null;
  plannedDate: string;
  deliveredAt: string | null;
  displayStatus: MilestoneDisplayStatusV2;
  /** Whole days past the planned date for a milestone not yet delivered.
   *  Null when the milestone is not overdue. */
  daysOverdue: number | null;
  paymentAmount: number | null;
}

export interface SummaryMilestonesSection {
  status: "ok";
  /** Value of milestones that have been accepted — completion, which is not
   *  the same thing as payment. */
  valueCompleted: number;
  valueTotal: number;
  valueCompletedPct: number | null;
  acceptedCount: number;
  /** EVERY milestone on the contract, not the length of `rows` — `rows`
   *  carries only the overdue and next-30-days subset. */
  totalCount: number;
  slippedCount: number;
  dueNext30Days: number;
  worstSlipDays: number | null;
  rows: SummaryMilestoneRow[];
  /** Milestones needing attention before the row cap — what `rows` is out of. */
  attentionCount: number;
  truncated: boolean;
}

export interface SummaryDeliverablesSection {
  status: "ok";
  onTimeRate: number | null;
  onTimeCount: number;
  totalResolved: number;
  overdueCount: number;
  awaitingReviewCount: number;
  /** Occurrences, not recurring parent templates. */
  leafCount: number;
  ragFlags: { green: number; amber: number; red: number };
}

export interface SummaryKpisSection {
  status: "ok";
  lastClosedMonth: ReportMonth | null;
  lastMonthMet: number;
  lastMonthMeasured: number;
  lastMonthWeightedPct: number | null;
  breachCount: number;
  atRiskCount: number;
  kpiCount: number;
  creditsRecordedTotal: number;
  creditsRecordedCount: number;
  reportsOutstanding: number;
}

export interface SummaryIssueRow {
  id: string;
  issueRef: string | null;
  title: string;
  severity: IssueSeverity | null;
  issueStatus: IssueStatus | null;
  party: string | null;
  ageDays: number;
}

export interface SummaryIssuesSection {
  status: "ok";
  openCount: number;
  criticalOpenCount: number;
  overdueActionCount: number;
  lifetimeRaised: number;
  lifetimeClosed: number;
  rows: SummaryIssueRow[];
  /** The honest denominator for `rows` — every open issue, so a truncated
   *  table can read "Showing 50 of 63". */
  totalCount: number;
  truncated: boolean;
}

export interface SummaryVariationRow {
  id: string;
  name: string;
  variationStatus: string;
  effectiveDate: string | null;
  valueImpact: number | null;
  targets: VariationTarget[];
}

export interface SummarySowRow {
  id: string;
  number: string | null;
  name: string;
  sowStatus: string;
  originalValue: number;
  startDate: string | null;
  endDate: string | null;
}

export interface SummaryVariationsSection {
  status: "ok";
  approvedValueImpact: number;
  variations: SummaryVariationRow[];
  sows: SummarySowRow[];
  gstBasis: "ex-GST";
  /** Describes `variations` only — the SOW list is never truncated. */
  totalCount: number;
  truncated: boolean;
}

export type InsuranceCondition =
  | "expired"
  | "expiring-soon"
  | "current"
  | "upcoming";

export interface SummaryInsuranceRow {
  id: string;
  subjectName: string | null;
  category: InsuranceCategory | null;
  insuranceTypes: InsuranceType[] | null;
  provider: string | null;
  policyNumber: string | null;
  coverageValue: number | null;
  minimumLimit: number | null;
  startDate: string | null;
  expiryDate: string | null;
  condition: InsuranceCondition;
  /** A policy with a qualifying active successor has been renewed, and must
   *  not read as a compliance failure just because the original has lapsed. */
  superseded: boolean;
  daysToExpiry: number | null;
}

export interface SummaryInsuranceSection {
  status: "ok";
  expiredCount: number;
  expiringSoonCount: number;
  rows: SummaryInsuranceRow[];
  totalCount: number;
  truncated: boolean;
}

export interface SummaryReportModel {
  generatedAt: string;
  /**
   * Discriminated so a commentary READ FAILURE is distinguishable from "no
   * record yet" — an absent record is `status: 'ok'` with version-0 defaults,
   * which renders as empty editable fields.
   */
  commentary: ({ status: "ok" } & ReportCommentary) | SectionUnavailable;
  sections: {
    band: SummaryBandSection | SectionUnavailable;
    essentials: SummaryEssentialsSection | SectionUnavailable;
    financial: SummaryFinancialSection | SectionUnavailable;
    milestones: SummaryMilestonesSection | SectionUnavailable;
    deliverables: SummaryDeliverablesSection | SectionUnavailable;
    kpis: SummaryKpisSection | SectionUnavailable;
    issues: SummaryIssuesSection | SectionUnavailable;
    variations: SummaryVariationsSection | SectionUnavailable;
    insurance: SummaryInsuranceSection | SectionUnavailable;
  };
}

// ─── Report PDF export ────────────────────────────────────────────────────
// Mirrors backend/src/models/contractExport.ts (report kind). A report export
// rides the contract export route with `kind: "report"`.

export const REPORT_EXPORT_VIEWS = ["summary", "delivery", "kpi", "financial"] as const;
export type ReportExportView = (typeof REPORT_EXPORT_VIEWS)[number];

export const REPORT_EXPORT_SECTION_KEYS = [
  "band",
  "commentary",
  "essentials",
  "financial",
  "milestones",
  "deliverables",
  "kpis",
  "issues",
  "variations",
  "insurance",
] as const;
export type ReportExportSectionKey = (typeof REPORT_EXPORT_SECTION_KEYS)[number];
export type ReportExportSections = Record<ReportExportSectionKey, boolean>;

/** Summary section headings exactly as the summary cards title them — the
 *  export dialog's toggles and the PDF's headings use the same words. */
export const REPORT_EXPORT_SECTION_LABELS: Record<ReportExportSectionKey, string> = {
  band: "Executive summary",
  commentary: "Manager commentary",
  essentials: "Contract essentials",
  financial: "Financial position",
  milestones: "Milestones",
  deliverables: "Deliverables and obligations",
  kpis: "KPIs and service levels",
  issues: "Issues",
  variations: "Variations and SOWs",
  insurance: "Insurance compliance",
};

/** What a report export row was asked for — drives its export-history label. */
export interface ReportExportOptions {
  view: ReportExportView;
  range: ReportRange;
  recordType?: DeliveryRecordType;
  sections?: ReportExportSections;
}

export interface CreateReportExportInput {
  kind: "report";
  /** Minted per request so a retried POST reuses the export already started. */
  requestToken: string;
  view: ReportExportView;
  range: ReportRange;
  recordType?: DeliveryRecordType;
  sections?: ReportExportSections;
}
